import {
  Checkbox,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CircularIndeterminate from "components/MDLoading";
import MDTypography from "components/MDTypography";
import { updateHelpMessage } from "store/slice/places/placeSlice";
import { getPlaces } from "store/slice/places/placeSlice";
import MDBox from "components/MDBox";
import { updatePlaceSettingsValidationSchema } from "services/validation";

const UpdateSettings = (props) => {
  const formikRef = useRef(null);
  const { dialogOpen, onClose, data } = props;
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });


  const handleEditMessage = async (data) => {
    try {
      setIsLoading(true);
      const res = await dispatch(updateHelpMessage(data)).unwrap();
      const success = res?.success;
      setNotification({
        ...notification,
        color: success ? "success" : "error",
        title: success ? "Success" : "Error",
        content: res?.message || "An unexpected error occurred.",
        icon: success ? "check" : "warning",
        show: true,
      });
      if (success) {
        dispatch(getPlaces());
        onClose();
      }
    } catch (err) {
      console.error("Error updating message:", err);
      setNotification({
        ...notification,
        color: "error",
        title: "Error",
        content: err?.message || "An error occurred while updating message.",
        icon: "warning",
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text={"Please wait..!"}
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle={`Place Settings - (${data?.parkingCode})`}
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon={true}
        maxWidth="xl"
      >
        <DialogContent>
          <Formik
            innerRef={formikRef}
            validationSchema={updatePlaceSettingsValidationSchema}
            initialValues={{
              placeId: data?._id,
              helpText: data?.helpText,
              collectSpaceNumber: data?.collectSpaceNumber || false,
              isVerifyLicensePlate: data?.isVerifyLicensePlate || false,
              statementDescriptor: data?.statementDescriptor || "",
              parkingType: data?.isVipParking
                ? "vip"
                : data?.isUnitParkingFlow
                ? "unit"
                : "normal",
              additionalSpaces: data?.additionalSpaces || 0,
            }}
            onSubmit={(value, action) => {
              const payload = {
                placeId: data?._id,
                helpText: value?.helpText || "",
                collectSpaceNumber: value?.collectSpaceNumber,
                additionalSpaces: value?.additionalSpaces,
                statementDescriptor: value?.statementDescriptor,
                isVipParking: value.parkingType === "vip",
                isUnitParkingFlow: value.parkingType === "unit",
                isVerifyLicensePlate: value?.isVerifyLicensePlate,
              };
              handleEditMessage(payload);
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <MDTypography variant="caption2">
                  {`${data?.google?.formatted_address} - (${data?.parkingCode})`}
                </MDTypography>
                <Divider />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDInput
                      name="helpText"
                      value={props.values.helpText}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Help Text For Receipt"
                      error={
                        props.errors.helpText && props.touched.helpText
                          ? true
                          : false
                      }
                      success={
                        props.errors.helpText && props.touched.helpText
                          ? false
                          : true
                      }
                      helperText={
                        props.errors.helpText && props.touched.helpText
                          ? props.errors.helpText
                          : null
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDInput
                      name="statementDescriptor"
                      value={props.values.statementDescriptor}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Statement Descriptor"
                      error={
                        props.errors.statementDescriptor &&
                        props.touched.statementDescriptor
                          ? true
                          : false
                      }
                      success={
                        props.errors.statementDescriptor &&
                        props.touched.statementDescriptor
                          ? false
                          : true
                      }
                      helperText={
                        props.errors.statementDescriptor &&
                        props.touched.statementDescriptor
                          ? props.errors.statementDescriptor
                          : null
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography variant="button" fontWeight="regular">
                        &nbsp;&nbsp;Is this location a pay by space?&nbsp;
                      </MDTypography>
                      <Checkbox
                        checked={props.values.collectSpaceNumber}
                        onChange={(e) => {
                          props.handleChange(e);
                          props.setFieldValue(
                            "collectSpaceNumber",
                            e.target.checked
                          );
                        }}
                        inputProps={{
                          "aria-label": "controlled",
                        }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset">
                      <MDTypography mb={1} variant="body2" fontWeight="regular">
                        Choose Parking Flow
                      </MDTypography>

                      <RadioGroup
                        name="parkingType"
                        sx={{ flexDirection: "row" }}
                        value={props.values.parkingType}
                        onChange={(e) =>
                          props.setFieldValue("parkingType", e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="normal"
                          control={<Radio />}
                          label="Normal Parking"
                        />
                        <FormControlLabel
                          value="vip"
                          control={<Radio color="primary" />}
                          label="VIP Parking"
                        />
                        <FormControlLabel
                          value="unit"
                          control={<Radio />}
                          label="Unit Parking"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="select-spaces-label">
                        Additional Spaces
                      </InputLabel>
                      <Select
                        labelId="select-spaces-label"
                        label="Additional Spaces"
                        value={props.values?.additionalSpaces}
                        onChange={(e) => {
                          const count = Number(e.target.value);
                          props.setFieldValue("additionalSpaces", count);
                        }}
                      >
                        {Array.from({ length: 100 }, (_, i) => i).map((num) => (
                          <MenuItem key={num} value={num}>
                            {num}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography variant="button" fontWeight="regular">
                        &nbsp;&nbsp;Verify License plate&nbsp;
                      </MDTypography>
                      <Checkbox
                        checked={props.values.isVerifyLicensePlate}
                        onChange={(e) => {
                          props.handleChange(e);

                          props.setFieldValue(
                            "isVerifyLicensePlate",
                            e.target.checked
                          );
                        }}
                        inputProps={{
                          "aria-label": "controlled",
                        }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} className="text-right">
                    <MDButton
                      color={sidenavColor}
                      variant="contained"
                      type="submit"
                    >
                      Update
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default UpdateSettings;
